/**
 * 视频上传定时任务
 * 功能：读取IndexedDB视频缓存中不以 https://yuyuankj.cn 开头的视频，上传到七牛云
 *
 * 完整流程：
 * 1. 获取七牛上传凭证 (POST /api/video/qiniu/upload-signature)
 * 2. 直传七牛云
 * 3. 通知后端回调 (POST /api/video/qiniu/upload-callback)
 * 4. 删除缓存，更新前端
 *
 * 特点：
 * - 5秒间隔检查一次
 * - 线程池模式：最多2个并发处理线程
 * - 每个线程异步独立处理一条视频
 * - 不影响页面功能（后台异步执行）
 * - 防止重复处理（使用processingUrls记录）
 * - 不漏处理（队列机制，每次取最早未处理的）
 */

import { API_BASE_URL, TOKEN_KEY, QINIU_UPLOAD_API_URL } from '@/config/constants'
import { appVersionFetch } from '@/utils/appVersion'

const DB_NAME = 'VideoCacheDB'
const DB_VERSION = 1
const STORE_NAME = 'videos'

// 线程池配置
const MAX_CONCURRENT_UPLOADS = 2 // 最大并发上传数
const CHECK_INTERVAL_MS = 5000 // 检查间隔：5秒

// 调试日志开关
const DEBUG = true

/**
 * 调试日志输出
 */
const log = {
  info: (tag, message, data) => {
    if (DEBUG) {
      console.log(`[视频上传任务:${tag}] ${message}`, data !== undefined ? data : '')
    }
  },
  success: (tag, message, data) => {
    if (DEBUG) {
      console.log(`%c[视频上传任务:${tag}] ${message}`, 'color: #52c41a; font-weight: bold', data !== undefined ? data : '')
    }
  },
  warn: (tag, message, data) => {
    if (DEBUG) {
      console.warn(`[视频上传任务:${tag}] ${message}`, data !== undefined ? data : '')
    }
  },
  error: (tag, message, error) => {
    if (DEBUG) {
      console.error(`[视频上传任务:${tag}] ${message}`, error)
    }
  }
}

// 任务状态
let taskTimer = null
let updateCallback = null // 用于通知VideoList更新数据的回调

// 正在处理的URL集合（防止重复处理）
const processingUrls = new Set()

/**
 * 打开数据库
 */
const openDB = () => {
  return new Promise((resolve, reject) => {
    if (window.videoCacheDB) {
      resolve(window.videoCacheDB)
      return
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => {
      reject(new Error('打开数据库失败'))
    }

    request.onsuccess = (event) => {
      window.videoCacheDB = event.target.result
      resolve(window.videoCacheDB)
    }

    request.onupgradeneeded = (event) => {
      const database = event.target.result
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = database.createObjectStore(STORE_NAME, { keyPath: 'url' })
        objectStore.createIndex('timestamp', 'timestamp', { unique: false })
      }
    }
  })
}

/**
 * 获取所有需要上传的缓存视频
 * 筛选条件：
 * 1. URL不以 https://yuyuankj.cn 开头
 * 2. 不在正在处理集合中
 */
const getVideosToUpload = async () => {
  try {
    const database = await openDB()
    const transaction = database.transaction([STORE_NAME], 'readonly')
    const store = transaction.objectStore(STORE_NAME)

    return new Promise((resolve) => {
      const request = store.getAll()

      request.onsuccess = () => {
        const allVideos = request.result || []

        log.info('DB', `IndexedDB中共有 ${allVideos.length} 个缓存视频`)

        // 筛选需要上传的视频：不是yuyuankj.cn且不在处理中
        const needUpload = allVideos.filter(video => {
          const isYuyuanUrl = video.url.startsWith('https://yuyuankj.cn')
          const isProcessing = processingUrls.has(video.url)
          const shouldUpload = !isYuyuanUrl && !isProcessing
          if (!shouldUpload) {
            log.info('Filter', `跳过: ${video.url.substring(0, 50)}... (yuyuankj: ${isYuyuanUrl}, processing: ${isProcessing})`)
          }
          return shouldUpload
        })

        // 按时间戳排序，处理最早缓存的
        needUpload.sort((a, b) => a.timestamp - b.timestamp)

        log.info('DB', `筛选出 ${needUpload.length} 个待上传视频`, needUpload.map(v => ({
          url: v.url.substring(0, 60) + '...',
          size: (v.blob?.size / 1024 / 1024).toFixed(2) + 'MB',
          timestamp: new Date(v.timestamp).toLocaleString()
        })))

        resolve(needUpload)
      }

      request.onerror = () => {
        log.error('DB', '获取缓存视频失败', request.error)
        resolve([])
      }
    })
  } catch (error) {
    log.error('DB', '读取视频缓存异常', error)
    return []
  }
}

/**
 * 删除指定的缓存视频
 */
const deleteCachedVideo = async (videoUrl) => {
  try {
    const database = await openDB()
    const transaction = database.transaction([STORE_NAME], 'readwrite')
    const store = transaction.objectStore(STORE_NAME)

    return new Promise((resolve) => {
      const request = store.delete(videoUrl)

      request.onsuccess = () => {
        log.success('DB', `已删除缓存: ${videoUrl.substring(0, 60)}...`)
        resolve(true)
      }

      request.onerror = () => {
        log.error('DB', `删除缓存失败: ${videoUrl.substring(0, 60)}...`, request.error)
        resolve(false)
      }
    })
  } catch (error) {
    log.error('DB', '删除缓存异常', error)
    return false
  }
}

/**
 * 从URL中提取文件名
 */
const extractFileName = (url) => {
  try {
    const urlObj = new URL(url)
    const pathname = urlObj.pathname
    const parts = pathname.split('/')
    const lastPart = parts[parts.length - 1]
    if (lastPart && lastPart.includes('.')) {
      return lastPart
    }
    return `video_${Date.now()}.mp4`
  } catch {
    return `video_${Date.now()}.mp4`
  }
}

/**
 * 步骤1: 获取七牛上传凭证
 * @param {string} fileName - 文件名
 * @param {string} videoUrl - 原始视频URL
 * @returns {Promise<{token: string, key: string, taskId: string}|{error: true, code?: number, message: string}>}
 */
const getQiniuUploadSignature = async (fileName, videoUrl) => {
  const token = localStorage.getItem(TOKEN_KEY)
  const requestId = `SIG-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

  log.info('步骤1', `开始获取七牛凭证 [${requestId}]`, {
    fileName,
    videoUrl: videoUrl.substring(0, 80) + '...'
  })

  try {
    const startTime = Date.now()
    const response = await appVersionFetch(`${API_BASE_URL}/api/video/qiniu/upload-signature`, {
      method: 'POST',
      headers: {
        'satoken': token || '',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ fileName, videoUrl })
    })

    const result = await response.json()
    const duration = Date.now() - startTime

    log.info('步骤1', `后端响应 [${requestId}] (${duration}ms)`, {
      status: response.status,
      code: result.code,
      message: result.message
    })

    if (result.code === 200 && result.data) {
      log.success('步骤1', `获取七牛凭证成功 [${requestId}]`, {
        taskId: result.data.taskId,
        key: result.data.key,
        tokenPrefix: result.data.token?.substring(0, 20) + '...'
      })
      return {
        token: result.data.token,
        key: result.data.key,
        taskId: result.data.taskId
      }
    } else {
      log.warn('步骤1', `获取七牛凭证失败 [${requestId}]`, {
        code: result.code,
        message: result.message,
        alreadyUploaded: result.data?.alreadyUploaded,
        finalVideoUrl: result.data?.finalVideoUrl
      })
      // 返回错误信息，由调用方判断是否需要删除缓存
      return { error: true, code: result.code, message: result.message }
    }
  } catch (error) {
    log.error('步骤1', `获取七牛凭证异常 [${requestId}]`, error)
    return { error: true, message: error.message }
  }
}

/**
 * 步骤2: 直传七牛云
 * @param {Blob} blob - 视频数据
 * @param {string} token - 七牛上传token
 * @param {string} key - 上传的文件key
 * @returns {Promise<{hash: string, key: string}|null>}
 */
const uploadToQiniu = async (blob, token, key) => {
  const requestId = `UPLOAD-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  const file = new File([blob], extractFileName(key), { type: blob.type || 'video/mp4' })
  const fileSizeMB = (file.size / 1024 / 1024).toFixed(2)

  log.info('步骤2', `开始上传七牛 [${requestId}]`, {
    key,
    fileName: file.name,
    fileSize: `${fileSizeMB}MB`,
    fileType: file.type,
    uploadUrl: QINIU_UPLOAD_API_URL,
    tokenPrefix: token?.substring(0, 30) + '...',
    tokenLength: token?.length
  })

  const formData = new FormData()
  formData.append('file', file)
  formData.append('token', token)
  formData.append('key', key)

  // 打印 FormData 内容（调试用）
  log.info('步骤2', `FormData 内容 [${requestId}]`, {
    hasFile: !!formData.get('file'),
    hasToken: !!formData.get('token'),
    hasKey: !!formData.get('key'),
    keyValue: formData.get('key'),
    tokenValueLength: formData.get('token')?.length
  })

  try {
    const startTime = Date.now()
    const response = await fetch(QINIU_UPLOAD_API_URL, {
      method: 'POST',
      body: formData
    })

    // 获取响应文本
    const responseText = await response.text()
    const duration = Date.now() - startTime
    const speed = ((file.size / 1024 / 1024) / (duration / 1000)).toFixed(2)

    log.info('步骤2', `七牛响应 [${requestId}] (${duration}ms, ${speed}MB/s)`, {
      status: response.status,
      ok: response.ok,
      statusText: response.statusText,
      responseText: responseText.substring(0, 500)
    })

    // 尝试解析为 JSON
    let result
    try {
      result = JSON.parse(responseText)
    } catch (e) {
      log.error('步骤2', `七牛返回非JSON响应 [${requestId}]`, {
        responseText: responseText.substring(0, 1000)
      })
      return null
    }

    if (result.hash && result.key) {
      log.success('步骤2', `七牛上传成功 [${requestId}]`, {
        hash: result.hash,
        key: result.key,
        duration: `${duration}ms`,
        speed: `${speed}MB/s`
      })
      return { hash: result.hash, key: result.key }
    } else {
      log.error('步骤2', `七牛上传失败 [${requestId}]`, {
        error: result.error,
        response: result,
        status: response.status
      })
      return null
    }
  } catch (error) {
    log.error('步骤2', `七牛上传异常 [${requestId}]`, error)
    return null
  }
}

/**
 * 步骤3: 通知后端上传完成
 * @param {string} taskId - 任务ID
 * @param {string} key - 七牛返回的key
 * @param {string} hash - 七牛返回的hash
 * @returns {Promise<{finalVideoUrl: string}|null>}
 */
const notifyBackendUploadComplete = async (taskId, key, hash) => {
  const token = localStorage.getItem(TOKEN_KEY)
  const requestId = `CALLBACK-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

  log.info('步骤3', `开始通知后端 [${requestId}]`, {
    taskId,
    key,
    hash
  })

  try {
    const startTime = Date.now()
    const response = await appVersionFetch(`${API_BASE_URL}/api/video/qiniu/upload-callback`, {
      method: 'POST',
      headers: {
        'satoken': token || '',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ taskId, key, hash })
    })

    const result = await response.json()
    const duration = Date.now() - startTime

    log.info('步骤3', `后端响应 [${requestId}] (${duration}ms)`, {
      status: response.status,
      code: result.code,
      message: result.message,
      hasFinalVideoUrl: !!result.data?.finalVideoUrl
    })

    if (result.code === 200 && result.data?.finalVideoUrl) {
      log.success('步骤3', `后端回调成功 [${requestId}]`, {
        finalVideoUrl: result.data.finalVideoUrl,
        videoUrl: result.data.videoUrl
      })
      return { finalVideoUrl: result.data.finalVideoUrl }
    } else {
      log.error('步骤3', `后端回调失败 [${requestId}]`, {
        code: result.code,
        message: result.message,
        data: result.data
      })
      return null
    }
  } catch (error) {
    log.error('步骤3', `后端回调异常 [${requestId}]`, error)
    return null
  }
}

/**
 * 处理单个视频上传（独立线程执行）
 * 完整流程：获取凭证 -> 上传七牛 -> 通知后端 -> 删除缓存
 *
 * @param {Object} video - 视频缓存对象 { url, blob, timestamp }
 * @returns {Promise<boolean>} 是否上传成功
 */
const processSingleVideo = async (video) => {
  const threadId = `THREAD-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  const startTime = Date.now()

  // 标记为正在处理（防止重复）
  processingUrls.add(video.url)

  log.info('Thread', `▼▼▼ 线程启动 [${threadId}]`, {
    url: video.url.substring(0, 60) + '...',
    blobSize: (video.blob?.size / 1024 / 1024).toFixed(2) + 'MB',
    currentConcurrent: processingUrls.size,
    maxConcurrent: MAX_CONCURRENT_UPLOADS
  })

  try {
    const fileName = extractFileName(video.url)

    // ========== 步骤1: 获取七牛上传凭证 ==========
    log.info('Thread', `[${threadId}] → 步骤1: 获取七牛凭证`)
    const signatureResult = await getQiniuUploadSignature(fileName, video.url)

    // 如果获取凭证失败，判断是否需要删除缓存
    if (signatureResult?.error) {
      log.warn('Thread', `[${threadId}] 步骤1失败: ${signatureResult.message}`)
      // 如果后端返回任务已完成/视频已上传等状态，直接删除缓存
      if (signatureResult.code === 400 || signatureResult.message?.includes('已上传') ||
          signatureResult.message?.includes('已完成') || signatureResult.message?.includes('不存在')) {
        log.info('Thread', `[${threadId}] 视频无需上传，删除缓存`)
        await deleteCachedVideo(video.url)
      }
      return false
    }

    const { token, key, taskId } = signatureResult
    log.info('Thread', `[${threadId}] 步骤1成功: taskId=${taskId}, key=${key}`)

    // ========== 步骤2: 直传七牛云 ==========
    log.info('Thread', `[${threadId}] → 步骤2: 上传七牛云`)
    const qiniuResult = await uploadToQiniu(video.blob, token, key)

    if (!qiniuResult) {
      log.warn('Thread', `[${threadId}] 步骤2失败: 七牛上传失败，稍后重试`)
      return false
    }

    log.info('Thread', `[${threadId}] 步骤2成功: hash=${qiniuResult.hash}`)

    // ========== 步骤3: 通知后端回调 ==========
    log.info('Thread', `[${threadId}] → 步骤3: 通知后端`)
    const callbackResult = await notifyBackendUploadComplete(taskId, qiniuResult.key, qiniuResult.hash)

    if (!callbackResult) {
      log.warn('Thread', `[${threadId}] 步骤3失败: 后端回调失败，稍后重试`)
      return false
    }

    log.info('Thread', `[${threadId}] 步骤3成功: ${callbackResult.finalVideoUrl}`)

    // ========== 步骤4: 删除缓存，通知前端 ==========
    log.info('Thread', `[${threadId}] → 步骤4: 清理缓存`)
    const deleteSuccess = await deleteCachedVideo(video.url)

    if (updateCallback && typeof updateCallback === 'function') {
      log.info('Thread', `[${threadId}] 通知前端更新`)
      updateCallback(new Map([[video.url, callbackResult.finalVideoUrl]]))
    }

    const duration = Date.now() - startTime
    log.success('Thread', `▲▲▲ 线程完成 [${threadId}] (${(duration / 1000).toFixed(2)}s)`, {
      oldUrl: video.url.substring(0, 50) + '...',
      newUrl: callbackResult.finalVideoUrl,
      deleteSuccess
    })

    return true

  } catch (error) {
    const duration = Date.now() - startTime
    log.error('Thread', `▲▲▲ 线程异常 [${threadId}] (${(duration / 1000).toFixed(2)}s)`, error)
    return false
  } finally {
    // 处理完成（无论成功失败），从处理集合移除，释放线程槽位
    processingUrls.delete(video.url)
    log.info('Thread', `[${threadId}] 释放线程槽位，剩余并发: ${processingUrls.size}/${MAX_CONCURRENT_UPLOADS}`)
  }
}

/**
 * 执行单次任务检查
 * 检查是否有空闲线程槽位，如果有则启动新的处理线程
 */
const executeUploadTask = async () => {
  const checkId = `CHECK-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  const currentProcessing = processingUrls.size
  const availableSlots = MAX_CONCURRENT_UPLOADS - currentProcessing

  log.info('Schedule', `═══ 定时检查 [${checkId}] ═══`, {
    currentProcessing,
    maxConcurrent: MAX_CONCURRENT_UPLOADS,
    availableSlots
  })

  // 没有空闲线程槽位，跳过本次检查
  if (availableSlots <= 0) {
    log.warn('Schedule', `[${checkId}] 线程池已满，跳过本次检查`)
    return
  }

  try {
    // 获取需要上传的视频列表
    const videosToUpload = await getVideosToUpload()

    if (videosToUpload.length === 0) {
      log.info('Schedule', `[${checkId}] 无待上传视频`)
      return
    }

    // 计算可以启动的线程数（取空闲槽位和待处理数量的较小值）
    const threadsToStart = Math.min(availableSlots, videosToUpload.length)

    log.info('Schedule', `[${checkId}] 启动 ${threadsToStart} 个上传线程`, {
      pending: videosToUpload.length,
      starting: threadsToStart
    })

    // 启动多个独立线程处理（不等待完成，异步执行）
    for (let i = 0; i < threadsToStart; i++) {
      const video = videosToUpload[i]
      log.info('Schedule', `[${checkId}] 启动线程 ${i + 1}/${threadsToStart}`)
      // 不使用await，让每个线程独立异步执行
      processSingleVideo(video).catch(err => {
        log.error('Schedule', `[${checkId}] 线程异常: ${video.url.substring(0, 50)}...`, err)
      })
    }

  } catch (error) {
    log.error('Schedule', `[${checkId}] 执行异常`, error)
  }
}

/**
 * 启动定时任务
 * @param {number} intervalMs - 执行间隔（毫秒），默认5秒
 * @param {Function} onUpdate - 更新回调函数，接收 Map<oldUrl, newUrl>
 */
export const startVideoUploadTask = (intervalMs = CHECK_INTERVAL_MS, onUpdate = null) => {
  // 设置更新回调
  if (onUpdate) {
    updateCallback = onUpdate
    log.info('Task', '已设置更新回调函数')
  }

  // 如果任务已经在运行，先停止
  if (taskTimer) {
    log.warn('Task', '任务已在运行，先停止')
    stopVideoUploadTask()
  }

  log.success('Task', `★★★ 启动视频上传任务 ★★★`, {
    interval: `${intervalMs / 1000}s`,
    maxConcurrent: MAX_CONCURRENT_UPLOADS,
    uploadUrl: QINIU_UPLOAD_API_URL,
    debug: DEBUG
  })

  // 立即执行一次检查
  executeUploadTask().catch(err => {
    log.error('Task', '首次执行异常', err)
  })

  // 启动定时器（每5秒检查一次）
  taskTimer = setInterval(() => {
    executeUploadTask().catch(err => {
      log.error('Task', '定时执行异常', err)
    })
  }, intervalMs)
}

/**
 * 停止定时任务
 * 注意：不会中断正在进行的上传，只是停止启动新线程
 */
export const stopVideoUploadTask = () => {
  if (taskTimer) {
    clearInterval(taskTimer)
    taskTimer = null
    log.warn('Task', `●●● 停止视频上传任务 ●●●`, {
      stillProcessing: processingUrls.size,
      processingUrls: Array.from(processingUrls).map(u => u.substring(0, 50) + '...')
    })
  } else {
    log.info('Task', '任务未运行')
  }

  // 注意：不清理processingUrls，让已启动的线程继续完成
}

/**
 * 手动触发一次任务执行
 */
export const triggerVideoUploadTask = () => {
  return executeUploadTask()
}

/**
 * 获取任务运行状态
 */
export const isTaskRunning = () => {
  return taskTimer !== null
}

/**
 * 获取当前线程池状态
 */
export const getTaskStatus = () => {
  return {
    isTimerRunning: taskTimer !== null,
    currentConcurrent: processingUrls.size,
    maxConcurrent: MAX_CONCURRENT_UPLOADS,
    availableSlots: MAX_CONCURRENT_UPLOADS - processingUrls.size,
    processingUrls: Array.from(processingUrls)
  }
}

/**
 * 设置更新回调函数
 * 用于在VideoList.vue中注册回调，接收上传完成后的URL更新
 */
export const setUpdateCallback = (callback) => {
  updateCallback = callback
}
